import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  Lening,
  LeningAanwending,
  LeningAflossingsMethode,
  LeningGeldVerstrekker,
  OmschrijvingEnBedrag,
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {optionsLandcodesMetNLD} from '../../../gedeelde-componenten/gedeelde-componenten.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type LeningFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  rekeningNummer: FormControl<string | null>;
  bedrag: FormControl<number | null>;
  landCode: FormControl<string | null>;
  aanwending: FormControl<LeningAanwending | null>;
  geldVerstrekker: FormControl<LeningGeldVerstrekker | null>;
  wozNummerWaarLeningBijHoort: FormControl<string | null>;
  percentageBox1: FormControl<number | null>;
  hoofdsom0101: FormControl<number | null>;
  hoofdsom3112: FormControl<number | null>;
  rente: FormControl<number | null>;
  kosten: FormArray<OmschrijvingBedragFormGroup>,
  personeelsKorting: FormControl<number | null>;
  aanvangsDatum: FormControl<Date | null>;
  eindDatum: FormControl<Date | null>;
  oorspronkelijkeHoofdsom: FormControl<number | null>;
  rentePercentageDecimalen: FormControl<number | null>;
  aflossingsMethode: FormControl<LeningAflossingsMethode | null>;
  naamLeninggever: PersoonFormGroup;
  adresLeninggever: AdresFormGroup;
}>;

type PersoonFormGroup = FormGroup<{
  roepnaam: FormControl<string | null>;
  voorletters: FormControl<string | null>;
  tussenvoegsels: FormControl<string | null>;
  achternaam: FormControl<string | null>;
  bsn: FormControl<string | null>;
  geboortedatum: FormControl<Date | null>;
  overlijdensdatum: FormControl<Date | null>;
  landCodeNationaliteit: FormControl<string | null>;
}>;

type AdresFormGroup = FormGroup<{
  straat: FormControl<string | null>;
  huisNummer: FormControl<number | null>;
  huisNummerToevoeging: FormControl<string | null>;
  postcode: FormControl<string | null>;
  woonplaats: FormControl<string | null>;
  landCode: FormControl<string | null>;
}>;

type OmschrijvingBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-eigenwoning-schulden',
  templateUrl: './eigenwoning-schulden.component.html',
  imports: [
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiAlertModule,
    SharedUiDialogModule,
    SharedUiButtonModule,
    CommonModule
  ],
  styleUrls: ['./eigenwoning-schulden.component.scss']
})
export class EigenwoningSchuldenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  protected readonly LeningGeldVerstrekker = LeningGeldVerstrekker;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public isDefAangifte = false;
  public dialogVisible = false;

  public formGroup = new FormGroup({
    leningen: new FormArray<LeningFormGroup>([]),
  });

  public soortLeningGeldVerstrekker: SelectFormControlItem<string>[] = [
    {value: LeningGeldVerstrekker.BANK, label: 'Bank'},
    {value: LeningGeldVerstrekker.PENSIOENFONDS, label: 'Pensioenfonds'},
    {value: LeningGeldVerstrekker.VERZEKERAAR, label: 'Verzekeraar'},
    {value: LeningGeldVerstrekker.NIET_ADMINISTRATIEPLICHTIGE, label: 'Familie/Eigen BV'}
  ];

  public soortLeningAflossingsMethode: SelectFormControlItem<string>[] = [
    {value: LeningAflossingsMethode.ANNUITAIR, label: 'Annuïtair'},
    {value: LeningAflossingsMethode.LINEAIR, label: 'Lineair'},
    {value: LeningAflossingsMethode.OVERIG, label: 'Overig'}
  ];

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((data) => {
        this.isDefAangifte = data.taxType === 'DA';
        this.updateForm(data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.leningen || []);
      });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () => this.leningen.getRawValue().map(w => ({
      rente: w.rente,
      percentageBox1: w.percentageBox1,
    }));

    this.changeSubscriptions.add(
      this.leningen.valueChanges.pipe(
        debounceTime(500),
        tap(() => this.saveForm()),
        map(() => extractRelevant()),
        distinctUntilChanged((prev, curr) =>
          JSON.stringify(prev) === JSON.stringify(curr)
        ),
        switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe()
    );
  }

  get leningen(): FormArray<LeningFormGroup> {
    return this.formGroup.controls.leningen;
  }

  addRij(): void {
    this.leningen.push(this.createFormGroup(), {emitEvent: false});
  }

  deleteRij(index: number): void {
    this.leningen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  createFormGroup(item?: Lening): FormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      rekeningNummer: new FormControl<string | null>(item?.rekeningNummer ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
      landCode: new FormControl<string | null>(item?.landCode ?? null),
      aanwending: new FormControl<LeningAanwending | null>(item?.aanwending ?? null),
      geldVerstrekker: new FormControl<LeningGeldVerstrekker | null>(item?.geldVerstrekker ?? null),
      wozNummerWaarLeningBijHoort: new FormControl<string | null>(item?.wozNummerWaarLeningBijHoort ?? null),
      percentageBox1: new FormControl<number | null>(item?.percentageBox1 ?? 100.00),
      hoofdsom0101: new FormControl<number | null>(item?.hoofdsom0101 ?? null),
      hoofdsom3112: new FormControl<number | null>(item?.hoofdsom3112 ?? null),
      rente: new FormControl<number | null>(item?.rente ?? null),
      kosten: new FormArray<OmschrijvingBedragFormGroup>(
        (item?.kosten ?? []).map(k => this.createOmschrijvingBedragFormGroup(k))
      ),
      personeelsKorting: new FormControl<number | null>(item?.personeelsKorting ?? null),
      aanvangsDatum: new FormControl<Date | null>(item?.aanvangsDatum ?? null),
      eindDatum: new FormControl<Date | null>(item?.eindDatum ?? null),
      oorspronkelijkeHoofdsom: new FormControl<number | null>(item?.oorspronkelijkeHoofdsom ?? null),
      rentePercentageDecimalen: new FormControl<number | null>(item?.rentePercentageDecimalen ?? null),
      aflossingsMethode: new FormControl<LeningAflossingsMethode | null>(item?.aflossingsMethode ?? null),
      naamLeninggever: new FormGroup({
        roepnaam: new FormControl<string | null>(item?.naamLeninggever?.roepnaam ?? null),
        voorletters: new FormControl<string | null>(item?.naamLeninggever?.voorletters ?? null),
        tussenvoegsels: new FormControl<string | null>(item?.naamLeninggever?.tussenvoegsels ?? null),
        achternaam: new FormControl<string | null>(item?.naamLeninggever?.achternaam ?? null),
        bsn: new FormControl<string | null>(item?.naamLeninggever?.bsn ?? null),
        geboortedatum: new FormControl<Date | null>(item?.naamLeninggever?.geboortedatum ?? null),
        overlijdensdatum: new FormControl<Date | null>(item?.naamLeninggever?.overlijdensdatum ?? null),
        landCodeNationaliteit: new FormControl<string | null>(item?.naamLeninggever?.landCodeNationaliteit ?? "NLD"),
      }),
      adresLeninggever: new FormGroup({
        straat: new FormControl<string | null>(item?.adresLeninggever?.straat ?? null),
        huisNummer: new FormControl<number | null>(item?.adresLeninggever?.huisNummer ?? null),
        huisNummerToevoeging: new FormControl<string | null>(item?.adresLeninggever?.huisNummerToevoeging ?? null),
        postcode: new FormControl<string | null>(this.formatPostcode(item?.adresLeninggever?.postcode) ?? null),
        woonplaats: new FormControl<string | null>(item?.adresLeninggever?.woonplaats ?? null),
        landCode: new FormControl<string | null>(item?.adresLeninggever?.landCode ?? null),
      })
    });
  }

  private createOmschrijvingBedragFormGroup(item?: OmschrijvingEnBedrag): OmschrijvingBedragFormGroup {
    return new FormGroup({
      omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
      bedrag: new FormControl<number | null>(item?.bedrag ?? null),
    });
  }

  private updateForm(data : Lening[]): void {
    const lijst = data || [];
    const formArray = this.leningen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {

    const leningen: Lening[] = this.formGroup.getRawValue().leningen
      .filter(item => item.rekeningNummer || item.bedrag || item.landCode || item.aanwending || item.geldVerstrekker || item.wozNummerWaarLeningBijHoort || item.percentageBox1 || item.hoofdsom0101 || item.hoofdsom3112 || item.rente || (item.kosten && item.kosten.length > 0) || item.personeelsKorting || item.aanvangsDatum || item.eindDatum || item.oorspronkelijkeHoofdsom || item.rentePercentageDecimalen || item.aflossingsMethode)
      .map(item => {
        const geldVerstrekker = this.isDefAangifte ? item?.geldVerstrekker! : LeningGeldVerstrekker.BANK;
        const isNietAdministratieplichtige = geldVerstrekker === LeningGeldVerstrekker.NIET_ADMINISTRATIEPLICHTIGE;

        return {
          omschrijving: item.omschrijving ?? undefined,
          rekeningNummer: item?.rekeningNummer!,
          bedrag: item?.bedrag!,
          landCode: item?.landCode!,
          aanwending: item?.aanwending!,
          geldVerstrekker,
          wozNummerWaarLeningBijHoort: item?.wozNummerWaarLeningBijHoort!,
          percentageBox1: item?.percentageBox1 ?? 100.00,
          hoofdsom0101: item?.hoofdsom0101 ?? undefined,
          hoofdsom3112: item?.hoofdsom3112 ?? undefined,
          rente: item?.rente ?? undefined,
          kosten: item?.kosten?.length
            ? item.kosten.map(k => ({
              omschrijving: k.omschrijving ?? undefined,
              bedrag: k.bedrag ?? undefined,
            }))
            : undefined,
          personeelsKorting: item?.personeelsKorting ?? undefined,
          aanvangsDatum: item?.aanvangsDatum ?? undefined,
          eindDatum: item?.eindDatum ?? undefined,
          oorspronkelijkeHoofdsom: item?.oorspronkelijkeHoofdsom ?? undefined,
          rentePercentageDecimalen: item?.rentePercentageDecimalen ?? undefined,
          aflossingsMethode: item?.aflossingsMethode ?? undefined,
          naamLeninggever: isNietAdministratieplichtige ? {
            roepnaam: item?.naamLeninggever?.roepnaam ?? undefined,
            voorletters: item?.naamLeninggever?.voorletters ?? undefined,
            tussenvoegsels: item?.naamLeninggever?.tussenvoegsels ?? undefined,
            achternaam: item?.naamLeninggever?.achternaam ?? undefined,
            bsn: item?.naamLeninggever?.bsn ?? undefined,
            geboortedatum: item?.naamLeninggever?.geboortedatum ?? undefined,
            overlijdensdatum: item?.naamLeninggever?.overlijdensdatum ?? undefined,
            landCodeNationaliteit: item?.naamLeninggever?.landCodeNationaliteit ?? undefined,
          } : undefined,
          adresLeninggever: isNietAdministratieplichtige ? {
            straat: item?.adresLeninggever?.straat ?? undefined,
            huisNummer: item?.adresLeninggever?.huisNummer ?? undefined,
            huisNummerToevoeging: item?.adresLeninggever?.huisNummerToevoeging ?? undefined,
            postcode: item?.adresLeninggever?.postcode ?? undefined,
            woonplaats: item?.adresLeninggever?.woonplaats ?? undefined,
            landCode: item?.adresLeninggever?.landCode ?? undefined,
          } : undefined,
        };
      });

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          box1: {
            ...currentEntity.aangifte.box1,
            belastbareInkomstenUitEigenWoning: {
              ...currentEntity.aangifte.box1?.belastbareInkomstenUitEigenWoning,
              leningen: leningen
            }
          }
        },
      } as IncomeTaxEntity;
    });
  }

  hasPercentageBox1LessThan100(): boolean {
    return this.leningen.controls.some(control => {
      const percentage = control.get('percentageBox1')?.value;
      if (percentage != null) {
        return percentage >= 0 && percentage < 100;
      } else return false
    });
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

    // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

    // Zorg voor 4 cijfers en 2 hoofdletters
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
      const letters = cleanedPostcode.substring(4, 6).replace(/[^a-zA-Z]/g, '').toUpperCase(); // Alleen letters
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
  }

  isLeningAtIndexValid(index: number): boolean {
    if (!this.leningen || index < 0 || index >= this.leningen.length) {
      return false;
    }

    const leningFormGroup = this.leningen.at(index) as FormGroup;
    const geldVerstrekker = leningFormGroup.get('geldVerstrekker')?.value;

    if (geldVerstrekker === LeningGeldVerstrekker.NIET_ADMINISTRATIEPLICHTIGE) {
      const aanvangsDatum = leningFormGroup.get('aanvangsDatum')?.value;
      const eindDatum = leningFormGroup.get('eindDatum')?.value;
      const oorspronkelijkeHoofdsom = leningFormGroup.get('oorspronkelijkeHoofdsom')?.value;
      const rentePercentageDecimalen = leningFormGroup.get('rentePercentageDecimalen')?.value;
      const aflossingsMethode = leningFormGroup.get('aflossingsMethode')?.value;

      return !!(
        aanvangsDatum &&
        eindDatum &&
        oorspronkelijkeHoofdsom !== undefined &&
        rentePercentageDecimalen !== undefined &&
        aflossingsMethode
      );
    }
    return true;
  }

  get isCurrentLeningValid(): boolean {
    return this.isLeningAtIndexValid(this.index);
  }
}
